import { DashboardState, ESdChangeReason, IAction, IActionDefinitionDto, IEvent } from "@kuika/kuika-cl-model";
import { CascaderOptionType } from "antd/es/cascader";
import _ from "lodash";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { ActionPanel, EActionPanelStatus } from "./action-panel";
import "./action-section.scss";

export interface IEventPanelState {}

export interface IEventPanelProps {
  event?: IEvent;
  eventName?: string;
  getActionDefinitionItems: () => CascaderOptionType[];
  getUpdatedActionDefinitionItems?: () => CascaderOptionType[];
  clipboardValue?: string | undefined;
  setClipboardValue?: (value: string) => void;
  preventActionRemoval?: boolean;
}

class EventPanel extends PureSdComponent<IEventPanelProps & IWithSDProps, IEventPanelState> {
  constructor(props: IEventPanelProps & IWithSDProps) {
    super(props);
  }

  componentDidMount = (): void => {
    this.updateDbSchemaIfNeeded();
  };

  isEventHasMananagedDbAction = () => {
    return (
      this.props.event?.actions.some((x) => x.label === "SaveRecord") ||
      this.props.event?.actions.some((x) => x.label === "DeleteRecord")
    );
  };

  updateDbSchemaIfNeeded = () => {
    if (this.props.sd.schemas.size === 0 && this.isEventHasMananagedDbAction()) {
      try {
        this.props.sd?.datasources?.map((datasource) => {
          this.props.sd?.getTables(datasource?.id);
        });
      } catch {}
    }
  };

  getSelectedKuikaIds = () => {
    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme ||
      this.props.sd.dashboardState === DashboardState.workFlow
    ) {
      return ["-1"];
    }
    return this.props.sd.selectedKuikaIds;
  };

  updateAction = (action: IAction, changeReason?: ESdChangeReason) => {
    const kuikaId = this.getSelectedKuikaIds()[0];
    if (this.props.event) {
      this.props.sd.saveActionToEvent(kuikaId, _.cloneDeep(action), this.props.event, changeReason);
    }
  };

  deleteAction = (action: IAction) => {
    const kuikaId = this.getSelectedKuikaIds()[0];
    if (this.props.event) {
      this.props.sd.deleteActionFromEvent(kuikaId, action, this.props.event);
    }
  };

  getEventName = () => {
    if (this.props.event) {
      if (this.props.event.eventName) return this.props.event.eventName;
      if ((this.props.event as any).name) return (this.props.event as any).name;
    }
    return this.props.eventName;
  };

  getActionDefinition = (actionDefinitionId: string | undefined): IActionDefinitionDto | undefined => {
    if (!actionDefinitionId) return undefined;
    return this.props.sd.actionDefinitions.find((x) => x.id === actionDefinitionId);
  };

  getActionDefinitions = (actions: IAction[]): Map<string, IActionDefinitionDto> => {
    const actionDefinitions: Map<string, IActionDefinitionDto> = new Map();

    actions.forEach((action) => {
      const actionDefinition = this.getActionDefinition(action.actionDefinitionId);
      if (actionDefinition && action.actionDefinitionId) {
        actionDefinitions.set(action.actionDefinitionId, actionDefinition);
      }
    });

    return actionDefinitions;
  };

  render() {
    if (this.props.event === undefined) {
      return <></>;
    }
    if (this.props.sd.dashboardState === DashboardState.workFlow && this.props.event.eventName === "pageInit") {
      return <></>;
    }
    const method = this.props.sd.getSelectedApiMethod();
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();
    const sJob = this.props.sd.getSelectedSj();
    // VTO: SelectedWF is also used in ScreenDesign, therefore control parameter is changed to DashboardState
    // const wf = this.props.sd.selectedWf;
    const wf = this.props.sd.dashboardState == DashboardState.workFlow;
    const actionDefinitions = this.getActionDefinitions(this.props.event?.actions ?? []);
    // Hoisted: the `event !== undefined` narrowing above does not survive into
    // the Droppable render callback below.
    const eventName: string = this.props.event.eventName;

    return (
      <>
        {!method && !sJob && !wf && !bleMethod && (
          <div className="kuika-action-section__trigger-label">
            {/* Coloured by action-section.scss (--kuika-svg-color on the label), with the label's text. */}
            <KuikaSvg name="bolt" type="common" width={11} height={11} />
            {this.props.event.eventName}
          </div>
        )}
        <Droppable droppableId={`droppable_${this.props.event.eventName}`}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} data-testid={`actionList_${eventName}`}>
              {this.props.event?.actions?.map((action, idx) => {
                return (
                  <Draggable key={`draggable_${action.id}`} draggableId={`draggable_${action.id}`} index={idx}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        data-testid="actionRow"
                      >
                        <ActionPanel
                          provided={provided}
                          eventName={this.getEventName()}
                          onActionSaved={this.updateAction}
                          actionPanelStatus={EActionPanelStatus.edit}
                          action={action}
                          actionDefinition={actionDefinitions.get(action.actionDefinitionId!)}
                          deleteAction={this.deleteAction}
                          getActionDefinitionItems={this.props.getActionDefinitionItems}
                          clipboardValue={this.props.clipboardValue}
                          setClipboardValue={this.props.setClipboardValue}
                          isDragging={snapshot.isDragging}
                          preventActionRemoval={this.props.preventActionRemoval}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ConfirmationMessageChanged ||
      changeReason === ESdChangeReason.StartingScreenIdChanged ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged ||
      changeReason === ESdChangeReason.RecurringIntervalChange
    ) {
      return true;
    }
    return false;
  };
}

const eventPanel = withSD<IEventPanelProps>(EventPanel);
export { eventPanel as EventPanel };
